import env from '@/core/config/env';

/**
 * Log Levels
 */
enum LogLevel {
    DEBUG = 'debug',
    INFO = 'info',
    WARN = 'warn',
    ERROR = 'error',
}

/**
 * Log Context Interface
 */
interface LogContext {
    userId?: string;
    feature?: string;
    action?: string;
    [key: string]: unknown;
}

/**
 * Logger Class
 * Handles application logging with different levels
 */
class Logger {
    private shouldLog(level: LogLevel): boolean {
        // In production, don't log debug messages
        if (env.ENVIRONMENT === 'production' && level === LogLevel.DEBUG) {
            return false;
        }
        return true;
    }

    private log(level: LogLevel, message: string, context?: LogContext) {
        if (!this.shouldLog(level)) return;

        const logData = {
            timestamp: new Date().toISOString(),
            level,
            message,
            environment: env.ENVIRONMENT,
            ...context,
        };

        // Console logging for development
        if (env.ENVIRONMENT === 'development') {
            const consoleMethod = level === 'debug' || level === 'info' ? 'log' : level;
            // eslint-disable-next-line no-console
            console[consoleMethod](`[${level.toUpperCase()}]`, message, context || '');
        }

        // Send to monitoring service in production
        if (env.ENVIRONMENT === 'production') {
            this.sendToMonitoring(logData);
        }
    }

    private sendToMonitoring(_logData: unknown) {
        // TODO: Integrate with monitoring service (Sentry, DataDog, etc.)
        // Example: Sentry.captureMessage(logData.message, { level: logData.level, extra: logData });
    }

    debug(message: string, context?: LogContext) {
        this.log(LogLevel.DEBUG, message, context);
    }

    info(message: string, context?: LogContext) {
        this.log(LogLevel.INFO, message, context);
    }

    warn(message: string, context?: LogContext) {
        this.log(LogLevel.WARN, message, context);
    }

    error(message: string, context?: LogContext) {
        this.log(LogLevel.ERROR, message, context);
    }
}

export const logger = new Logger();
export { LogLevel };
export type { LogContext };
